<template>
    <v-card :height="height" flat>
        <div v-if="!disabled"
             class="panel-body no-padding">
            <div class="dragdrop-filters-container">
                <filter-autocomplete
                    v-model="filters.name.value"
                    :items="namesWithUserEntered"
                    :placeholder="t_search"
                    style="min-width: 64px"
                    @update:userEnteredItems="userEnteredItemsMap.name = $event"/>
                <filter-autocomplete
                    v-model="filters.tags.value"
                    :items="tagsWithUserEntered"
                    :placeholder="t_tags"
                    super-tags
                    @update:userEnteredItems="userEnteredItemsMap.tags = $event"/>
                <filter-autocomplete
                    v-model="filters.media_type_key.value"
                    :items="mediaTypes"
                    :placeholder="t_type"
                    :user-entered-items-enabled="false"
                    item-text="text"/>
                <v-select-custom
                    v-model="sortOption"
                    :items="sortOptionList"
                    :aria-label="t_sort_by"
                    inline
                    style="flex-basis: 60px; flex-grow: 0">
                    <!--suppress VueUnrecognizedSlot -->
                    <template #selection="{item}">
                        <div class="dragdrop-sort-selection-container">
                            <i :class="[item.text[0]]"/>
                            <i :class="[item.text[1]]"/>
                        </div>
                    </template>
                    <!--suppress VueUnrecognizedSlot -->
                    <template #item="{item}">
                        <div class="dragdrop-sort-selection-container">
                            <i :class="[item.text[0]]"/>
                            <i :class="[item.text[1]]"/>
                        </div>
                    </template>
                </v-select-custom>
            </div>
            <div :style="{height: `${listHeight}px`}" class="scrollBox">
                <mediapool-list :add="permissionAdd"
                                :drag-drop="true"
                                :elementClass="elementsPerRowClass"
                                :files="sortedEntries"
                                :loading="loading"
                                :readonly="readonly"
                                :small="true"
                                classes="no-padding"
                                force-mode="tiles">
                </mediapool-list>
            </div>
        </div>
    </v-card>
</template>

<script>
    import dndTabItemMixin from '../../../../mixins/dndTabItemMixin';
    import mediapoolList from '../../mediapool/mediapoolList.vue';
    import {sortElementList} from '../../../../../utils/publish';
    import MediapoolService from '../../../../services/MediapoolService';
    import FilterAutocomplete from '../table/filter-autocomplete.vue';
    import {mediaTypeToSortOrderMap, mediaTypeToTranslationMap} from '../../../../../utils/types';

    export default {
        components: {
            FilterAutocomplete,
            mediapoolList
        },
        mixins: [
            dndTabItemMixin
        ],
        data() {
            return {
                mediapoolService: new MediapoolService(),

                /** @type {Record.<string, {matchMode: string, value: string[], num?: true, fieldName?: string}>} */
                filters: {
                    name: {
                        matchMode: 'containsArray_OR',
                        value: []
                    },
                    media_type_key: {
                        // Must be left at OR, because they will never overlap
                        // (an element will not be both 'picture' and 'video')
                        // and users will expect them to always act with OR filtering (any must match).
                        matchMode: 'containsArray_OR',
                        value: []
                    },
                    tags: {
                        matchMode: 'arrayContainsNameArray_OR',
                        value: []
                    }
                },

                /** Used by entriesLoaderMixin.js */
                t_data_load_error: Lang.langGet('Failed to load media files!'),

                t_type: Lang.langChoice('Type|Types', 2)
            };
        },
        computed: {
            sortedEntries() {
                /* Shallow copy array with 'slice' to create new instance, 'sort'
                 * modifies the array in-place and would not work properly on a
                 * computed property. This is also much more readable. */
                return sortElementList(this.renderedEntries.slice(), 'display_name', this.sortOption);
            },
            /** Used by dndTabItemMixin.js */
            localStorageKeyDndSortOption() {
                return `dnd-sort-mediapool-${Laravel.activeProject}`;
            },
            mediaTypes() {
                let returnValue = [];

                /* Push mediaType items into returnValue according to the predefined order of mediaTypes.
                 * Add translated text onto these items. Luckily the v-autocomplete component handles this
                 * and shows translated text of these "sortOrder" items in the dropdown, even though the
                 * actual items concatenated below do not have the 'text' translated property. */
                for (const mediaType in mediaTypeToSortOrderMap) {
                    if (this.fieldValuesMap.media_type_key?.some(mediaTypeKeyEntry => mediaTypeKeyEntry.value == mediaType)) {
                        returnValue.push({value: mediaType, text: mediaTypeToTranslationMap[mediaType]});
                    }
                }

                const mediaTypeItems = this.filteredItemsWithUserEntered(this.fieldValuesMap.media_type_key, null, true);

                if (mediaTypeItems) {
                    returnValue = returnValue.concat(mediaTypeItems);
                }

                return returnValue;
            }
        },
        methods: {
            loadMediaFiles() {
                return this.loadEntries(() => this.mediapoolService.getMediaFiles(false, true));
            },
            refreshLocalMediaFiles() {
                return this.loadEntries(this.entries);
            }
        },
        created() {
            this.loadMediaFiles();
        }
    }
</script>
